import { useAsync, UseAsyncStatus } from '@shared/async';
import { useErrorHandler } from '@shared/errors';
import { useModal } from '@shared/modal';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiOutlineArrowDownTray, HiOutlineArrowUpTray, HiOutlineCalculator, HiOutlineLink, HiOutlinePlusCircle, HiOutlineTrash } from 'react-icons/hi2';
import { Spinner } from '@/components-new/spinner';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DrugMarketDetailsFilters } from '@/components/drug-market-details-filters';
import { useFilters } from '@/components/filters';
import { TableEmptyPlaceholder, TableLoadingOverlay } from '@/components/loading';
import { RowPinSelector } from '@/components/row-pin-selector';
import { Button } from '@/components-new/button';
import { Checkbox, CheckboxGroup } from '@/components-new/checkbox';
import { DataPaginator } from '@/components-new/data-paginator';
import { DataTable, useDataTable } from '@/components-new/data-table';
import { DrugName } from '@/components-new/drugs';
import { UpdateOverridesRequest, useMarketBasketService } from '@/features/market-baskets/api/use-market-baskets-service';
import { CalculationDetails, MarketBasketDrugPackaging } from '@/features/market-baskets/types/market-basket';
import { MarketBasketOverridesFormInputs } from '@/features/market-baskets/types/market-basket-overrides-form-inputs';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useToggle } from '@/hooks/use-toggle';
import { notifySuccess, notifyWarn } from '@/lib/notification/notifications';
import { ACCENT_WEAK, STATUS_WARNING_WEAK } from '@/styles';
import { formatCurrency } from '@/utils/formatting';

import { useMarketBasketDetails } from '../../market-basket-details.provider';
import { AddDrugPackagingsDialog } from '../index/add-drug-packagings-dialog';
import { AssociateNdcsDialog } from '../index/associate-ndcs-dialog';
import { CalculationLogDialog } from '../index/calculation-log-dialog';
import { DeleteNdcsDialog } from '../index/delete-ndcs-dialog';
import { ImportMarketDetailsPanel } from '../index/import-market-details-panel';
import { InvalidNdcSelectionDialog } from '../index/invalid-ndc-selection-dialog';
import { resolvePinners, resolvePinning, RowPinKeys, rowPinOptions } from '../index/row-pinning';
import { drugPackagingsSorters, MarketBasketDrugPackagingSorterKeys } from '../index/sorters';
import { ActionsCell } from './actions-cell';
import { AmpCell } from './amp-cell';
import { BrandGenericStatusIndicator } from './brand-generic-status-indicator';
import { EditDrugPackagingDialog } from './edit-drug-packaging-dialog';
import { EditingActionsCell } from './editing-actions-cell';
import { EstimatedTotalUraCell } from './estimated-total-ura-cell';
import { EstimatedUroaCell } from './estimated-uroa-cell';
import { NdcCell } from './ndc-cell';
import { UraPercentWacCell } from './ura-percent-wac-cell';
import { WacPriceCell } from './wac-price-cell';
import { WacUnitPriceCell } from './wac-unit-price-cell';

const columnHelper = createColumnHelper<MarketBasketDrugPackaging>();

type SortState = { property: string; direction: 'asc' | 'desc' };

export const DrugMarketDetails = () => {
  const { marketBasket, loadingMarketBasket, loadMarketBasket } = useMarketBasketDetails();
  const { handleError } = useErrorHandler();
  const location = useLocation();
  const { deleteMarketBasketRows, exportMarketBasket, recalculate, updateOverrides } = useMarketBasketService();
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const { id } = useParams();

  const [ filters, setFilters ] = useFilters<{ ndc?: string, name?: string, product?: string }>(`market-baskets-${id}-drug-market-details`, {});
  const [ recalculating, setRecalculating ] = useState(false);
  const [ selectedNdcs, setSelectedNdcs ] = useState<string[]>([]);
  const [ selectedRowPinIds, setSelectedRowPinIds ] = useState<(string | number | readonly string[])[]>([RowPinKeys.belongsToMarketBasket]);
  const [ sort, setSort ] = useState<SortState>({ property: 'drug.name', direction: 'asc' });
  const [ showInvalidNdcSelectionDialog, toggleInvalidNdcSelectionDialog ] = useToggle(false);
  const [ showAssociateUnknownPackagingsDialog, toggleAssociateUnknownPackagingsDialog ] = useToggle(false);
  const [ showAddDrugPackagingsDialog, toggleAddDrugPackagingsDialog ] = useToggle(false);
  const [ showDeleteRowsDialog, toggleDeleteRowsDialog ] = useToggle(false);
  const [ showCalculationLogDialog, toggleCalculationLogDialog ] = useToggle(false);
  const [ drugPackagingToEdit, setDrugPackagingToEdit ] = useState<MarketBasketDrugPackaging>();
  const [ deletingRows, setDeletingRows ] = useState(false);
  const [ calculationDetails, setCalculationDetails ] = useState<CalculationDetails>();
  const [ drugPackagingsToOverride, setDrugPackagingsToOverride ] = useState<MarketBasketDrugPackaging[]>([]);
  const [lastNdcOverridden, setLastNdcOverridden] = useState<string>();

  const {
    control,
    setValue,
    getValues,
    getFieldState,
    resetField,
    formState: { dirtyFields }
  } = useForm<MarketBasketOverridesFormInputs>({ mode: 'all' });

  const { user } = useCurrentUser();

  // @ts-expect-error TS(2322): Type '{} | undefined' is not assignable to type 'M... Remove this comment to see the full error message
  const initialFormValues: MarketBasketOverridesFormInputs = useMemo(() => {
    if (!marketBasket) return;

    const { drugPackagings } = marketBasket;

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return drugPackagings.reduce(
      (previous, current) => {
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        const wac = current.currentWacPrice.overrideValue?.toString() ?? current.currentWacPrice.compendiumValue?.toString() ?? '';
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        const wacUnit = current.currentWacUnitPrice.overrideValue?.toString() ?? current.currentWacUnitPrice.compendiumValue?.toString() ?? '';
        const amp = current.amp.overrideValue?.toString() ?? current.amp.calculatedValue?.value?.toString() ?? '';
        const ura = current.totalUra.overrideValue?.toString() ?? current.totalUra.calculatedValue?.value?.toString() ?? '';
        const uroa = current.uroa.overrideValue?.toString() ?? current.uroa.calculatedValue?.value?.toString() ?? '';

        return { ...previous, [current.ndc]: { wac, wacUnit, amp, ura, uroa } };
      },
      {}
    );
    // TODO: revisit missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketBasket?.drugPackagings]);

  useEffect(() => {
    if (!initialFormValues) {
      return;
    }

    const dirtyNdcs = Object.keys(dirtyFields);

    Object.keys(initialFormValues)
      .filter(key => !dirtyNdcs.includes(key))
      .forEach(key => setValue(key, initialFormValues[key]));

    // TODO: revisit missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormValues]);

  const showEditDrugPackagingDialog = !!drugPackagingToEdit;

  const selectedDrugPackagings = useMemo(() => {
    return marketBasket?.drugPackagings?.filter(packaging => selectedNdcs.includes(packaging.ndc)) || [];
    // TODO: revisit missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNdcs]);

  const selectedRowPins = useMemo(() => {
    return rowPinOptions.filter(option => selectedRowPinIds.includes(option.value as any));
  }, [selectedRowPinIds]);

  const filteredPackagings = useMemo(() => {
    if (!marketBasket?.drugPackagings) {
      return [];
    }

    const { ndc, name, product } = filters;

    const filtered = marketBasket.drugPackagings.filter(packaging =>
      (!ndc || packaging.ndc?.includes(ndc))
      && (!name || packaging.name?.toLowerCase().includes(name.toLowerCase()))
      && (!product || packaging.drug?.name?.toLowerCase().includes(product.toLowerCase()))
    );

    const sorter = (items: MarketBasketDrugPackaging[]) =>
      (drugPackagingsSorters[sort.property as MarketBasketDrugPackagingSorterKeys] || drugPackagingsSorters.default)(items, sort.direction);

    const rowPinKeys = selectedRowPins.map(pin => pin.value as RowPinKeys);
    const rowPinners = resolvePinners(rowPinKeys);

    const sortedRowsForPinning = resolvePinning(rowPinners, sorter, filtered);
    const remainingRows = sorter(filtered.filter(packaging => !sortedRowsForPinning.includes(packaging)));

    return [...sortedRowsForPinning, ...remainingRows];
  }, [filters, marketBasket, sort, selectedRowPins]);

  useEffect(() => {
    if (location.hash === '#import') {
      openModal(
        {
          // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
          component: <ImportMarketDetailsPanel marketBasketId={parseInt(id)} getDrugMarketDetails={() => loadMarketBasket(parseInt(id))}/>,
          header: 'Import Market Details'
        },
        { onClose: () => closeImportModal() }
      );
    }
    // TODO: revisit missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const openAssociateNdcsDialog = async () => {
    const hasInvalidSelection = selectedDrugPackagings.some(packaging => packaging.drug !== null);

    if (hasInvalidSelection) {
      toggleInvalidNdcSelectionDialog();
      return;
    }

    toggleAssociateUnknownPackagingsDialog();
  };

  const requestDeleteRows = async () => {
    setDeletingRows(true);

    try {
      // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      const intId = parseInt(id);
      await deleteMarketBasketRows(intId, selectedNdcs);
      loadMarketBasket(intId);
    } catch (ex) {
      handleError(
        ex as Error,
        {
          title: 'Deletion Failed',
          message: 'We encountered an unexpected error while deleting the rows you selected.'
        }
      );
    } finally {
      setSelectedNdcs([]);
      setDeletingRows(false);
      toggleDeleteRowsDialog();
    }
  };

  const onExport = async () => {
    try {
      // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      const csv = await exportMarketBasket(parseInt(id));

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(csv.contents);
      link.download = csv.filename;
      link.click();
    } catch (ex) {
      handleError(
        ex as Error,
        {
          title: 'Export to CSV Failed',
          message: 'We encountered an unexpected error while exporting the market basket. Please try again or contact an administrator.'
        }
      );
    }
  };

  const onRecalculate = async () => {
    setRecalculating(true);

    try {
      // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
      const intId = parseInt(id);
      const result = await recalculate(intId);
      loadMarketBasket(intId);

      const warnings: string[] = result?.warnings ?? [];
      if (warnings.length > 0) {
        const ndcList = warnings
          .map(w => w.replace(/^NDC (\S+):.+$/, '$1'))
          .join(', ');
        notifyWarn({
          title: 'Recalculation Completed with Errors',
          message: `${warnings.length} row(s) could not be calculated and were skipped: ${ndcList}. These rows are highlighted in the table.`,
          options: { duration: 10000 },
        });
      } else {
        notifySuccess({ title: 'Recalculation Succeeded', message: 'Successfully recalculated.' });
      }
    } catch (ex) {
      handleError(
        ex as Error,
        {
          title: 'Recalculation Failed',
          message: 'We encountered an unexpected error while recalculating. Please try again or contact an administrator.',
        }
      );
    } finally {
      setRecalculating(false);
    }
  };

  const openImportModal = () => {
    navigate(location.pathname + '#import', { replace: false });
  };

  const closeImportModal = () => {
    navigate(location.pathname, { replace: false });
    closeModal();
  };

  const viewCalculationDetails = (calculationDetails: CalculationDetails) => {
    setCalculationDetails(calculationDetails);
    toggleCalculationLogDialog();
  };

  const handleRowPinSelectionChange = (selected: (string | number | readonly string[])[]) => {
    setSelectedRowPinIds(selected);
  };

  const onSelect = (ndcs: string[]) => {
    setSelectedNdcs(ndcs);
  };

  const handleAssociateUnknownPackagingsSuccess = () => {
    setSelectedNdcs([]);
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    loadMarketBasket(parseInt(id));
    toggleAssociateUnknownPackagingsDialog();
  };

  const handleEditMarketBasketDrugPackaging = (drugId: number, ndc: string) => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const drugPackaging = marketBasket
      .drugPackagings
      .find(packaging => packaging.drug?.id === drugId && packaging.ndc === ndc);

    setDrugPackagingToEdit(drugPackaging);
  };

  const cancelEditMarketBasketDrugPackaging = () => {
    setDrugPackagingToEdit(undefined);
  };

  const handleEditMarketBasketDrugPackagingSuccess = () => {
    setDrugPackagingToEdit(undefined);
    void onRecalculate();
  };

  const handleOverride = (ndc: string) => {
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const drugPackaging = marketBasket
      .drugPackagings
      .find(packaging => packaging.ndc === ndc);

    // @ts-expect-error TS(2322): Type 'MarketBasketDrugPackaging | undefined' is no... Remove this comment to see the full error message
    setDrugPackagingsToOverride([ ...drugPackagingsToOverride, drugPackaging ]);
  };

  const handleCancelOverride = (ndc: string) => {
    resetField(ndc, { defaultValue: initialFormValues[ndc] });

    setDrugPackagingsToOverride([
      ...drugPackagingsToOverride.filter(packaging => packaging.ndc !== ndc)
    ]);
  };

  const updateOverridesAsync = useAsync(updateOverrides);
  const updatingOverrides = updateOverridesAsync.status === UseAsyncStatus.Pending;

  useEffect(() => {
    const { status, error } = updateOverridesAsync;
    if (status === UseAsyncStatus.Pending || status === UseAsyncStatus.Idle) return;

    if (status === UseAsyncStatus.Error) {
      handleError(error, { title: 'Failed to Override.', message: 'Unable to override NDC prices.' });
      return;
    }

    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    resetField(lastNdcOverridden);

    setDrugPackagingsToOverride(
      drugPackagingsToOverride.filter(packaging => packaging.ndc !== lastNdcOverridden)
    );

    setLastNdcOverridden(undefined);

    notifySuccess({ title: 'Success', message: 'Overrides updated successfully.' });

    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    loadMarketBasket(parseInt(id));
    // TODO: revisit missing deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOverridesAsync.status]);

  const handleSaveOverride = async (ndc: string) => {
    if (lastNdcOverridden) {
      return;
    }

    const { wac, wacUnit, amp, ura, uroa } = getValues(ndc);

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const drugPackaging = marketBasket.drugPackagings.find(packaging => packaging.ndc === ndc);

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const includeWac = getFieldState(`${ndc}.wac`).isDirty || drugPackaging.currentWacPrice.overrideValue != null;
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const includeWacUnit = getFieldState(`${ndc}.wacUnit`).isDirty || drugPackaging.currentWacUnitPrice.overrideValue != null;
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const includeAmp = getFieldState(`${ndc}.amp`).isDirty || drugPackaging.amp.overrideValue != null;
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const includeUra = getFieldState(`${ndc}.ura`).isDirty || drugPackaging.totalUra.overrideValue != null;
    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    const includeUroa = getFieldState(`${ndc}.uroa`).isDirty || drugPackaging.uroa.overrideValue != null;

    const request: UpdateOverridesRequest = {
      wacPrice: includeWac && wac.length > 0 ? wac : undefined,
      wacUnitPrice: includeWacUnit && wacUnit.length > 0 ? wacUnit : undefined,
      amp: includeAmp && amp.length > 0 ? amp : undefined,
      totalUra: includeUra && ura.length > 0 ? ura : undefined,
      uroa: includeUroa && uroa.length > 0 ? uroa : undefined
    };

    setLastNdcOverridden(ndc);

    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    void updateOverridesAsync.execute(id, ndc, request);
  };

  const isOverridingRow = (ndc: string) => drugPackagingsToOverride.some(packaging => packaging.ndc === ndc);

  const assignedToThisClient = user.assignedClients?.some(it => it.id === marketBasket?.client?.id) ?? [];

  const allSelected = filteredPackagings.length > 0 && selectedNdcs.length === filteredPackagings.length;
  const someSelected = selectedNdcs.length > 0 && selectedNdcs.length < filteredPackagings.length;

  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      size: 40,
      header: () => (
        <div className="px-2">
          <CheckboxGroup>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={() => {
                onSelect(allSelected || someSelected ? [] : filteredPackagings.map(p => p.ndc));
              }}
            />
          </CheckboxGroup>
        </div>
      ),
      cell: ({ row }) => (
        <div className="px-2">
          <CheckboxGroup>
            <Checkbox
              checked={selectedNdcs.includes(row.original.ndc)}
              onChange={(checked) => {
                onSelect(
                  checked
                    ? [...selectedNdcs, row.original.ndc]
                    : selectedNdcs.filter(ndc => ndc !== row.original.ndc)
                );
              }}
            />
          </CheckboxGroup>
        </div>
      ),
    }),
    columnHelper.accessor('ndc', {
      header: 'NDC',
      cell: ({ row }) => <NdcCell ndc={row.original.ndc} needsAttention={row.original.needsAttention} missingFields={row.original.missingFields} />,
    }),
    columnHelper.accessor('name', {
      header: 'Description',
    }),
    columnHelper.accessor('drug', {
      header: 'Product',
      cell: ({ row }) => <DrugName drug={row.original.drug} />,
    }),
    columnHelper.accessor('brandGenericStatus', {
      header: 'Brand/Generic Status',
      cell: ({ row }) => (
        <BrandGenericStatusIndicator
          brandGenericStatus={row.original.brandGenericStatus}
          licenseType={row.original.licenseType}
        />
      ),
    }),
    columnHelper.accessor('currentWacPrice', {
      header: 'WAC Price',
      cell: ({ row }) => (
        <WacPriceCell
          ndc={row.original.ndc}
          wac={row.original.currentWacPrice}
          isOverriding={isOverridingRow(row.original.ndc)}
          control={control}
        />
      ),
    }),
    columnHelper.accessor('currentWacUnitPrice', {
      header: 'WAC Unit Price',
      cell: ({ row }) => (
        <WacUnitPriceCell
          ndc={row.original.ndc}
          wacUnit={row.original.currentWacUnitPrice}
          isOverriding={isOverridingRow(row.original.ndc)}
          control={control}
        />
      ),
    }),
    columnHelper.accessor('amp', {
      header: 'AMP',
      cell: ({ row }) => (
        <AmpCell
          ndc={row.original.ndc}
          amp={row.original.amp}
          isOverriding={!!(assignedToThisClient && isOverridingRow(row.original.ndc))}
          control={control}
        />
      ),
    }),
    columnHelper.accessor('acaFederalUpperLimitUnitPrice', {
      header: 'ACA-FUL Unit Price',
      cell: ({ row }) => row.original.acaFederalUpperLimitUnitPrice
        ? formatCurrency(row.original.acaFederalUpperLimitUnitPrice)
        : <>&mdash;</>,
    }),
    columnHelper.accessor('acaWeightedAverageManufacturersPrice', {
      header: 'ACA-WAMP Unit Price',
      cell: ({ row }) => row.original.acaWeightedAverageManufacturersPrice
        ? formatCurrency(row.original.acaWeightedAverageManufacturersPrice)
        : <>&mdash;</>,
    }),
    columnHelper.accessor('totalUra', {
      header: 'Estimated Total URA',
      cell: ({ row }) => (
        <EstimatedTotalUraCell
          ndc={row.original.ndc}
          ura={row.original.totalUra}
          isOverriding={!!(assignedToThisClient && isOverridingRow(row.original.ndc))}
          control={control}
          onViewCalculationDetails={viewCalculationDetails}
        />
      ),
    }),
    columnHelper.accessor('uraPercentWac', {
      header: 'URA % WAC',
      cell: ({ row }) => (
        <UraPercentWacCell
          uraPercentWac={row.original.uraPercentWac}
          onViewCalculationDetails={viewCalculationDetails}
        />
      ),
    }),
    columnHelper.accessor('uroa', {
      header: 'Estimated UROA',
      cell: ({ row }) => (
        <EstimatedUroaCell
          ndc={row.original.ndc}
          uroa={row.original.uroa}
          isOverriding={!!(assignedToThisClient && isOverridingRow(row.original.ndc))}
          control={control}
          onViewCalculationDetails={viewCalculationDetails}
        />
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      cell: ({ row }) => isOverridingRow(row.original.ndc)
        ? (
          <EditingActionsCell
            disabled={!!lastNdcOverridden}
            saving={updatingOverrides && lastNdcOverridden === row.original.ndc}
            onCancel={() => handleCancelOverride(row.original.ndc)}
            onSave={() => handleSaveOverride(row.original.ndc)}
          />
        ) : (
          <ActionsCell
            {...row.original}
            onEditClick={handleEditMarketBasketDrugPackaging}
            onOverrideClick={handleOverride}
          />
        ),
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [filteredPackagings, selectedNdcs, allSelected, someSelected, drugPackagingsToOverride, lastNdcOverridden, updatingOverrides, assignedToThisClient, control]);

  const table = useDataTable({
    data: filteredPackagings,
    columns,
    getRowId: (row) => row.ndc,
    enablePagination: true,
    enableSorting: false,
    tableBodyRowProps: ({ row }) => ({
      className: row.original.needsAttention
        ? `bg-[${STATUS_WARNING_WEAK}]`
        : row.original.belongsToMarketBasketDrug
          ? `bg-[${ACCENT_WEAK}]`
          : '',
      style: row.original.needsAttention
        ? { backgroundColor: STATUS_WARNING_WEAK }
        : row.original.belongsToMarketBasketDrug
          ? { backgroundColor: ACCENT_WEAK }
          : {},
    }),
  });

  return (
    <div>
      <div>
        <DrugMarketDetailsFilters defaultValue={filters} onApplyFilters={setFilters}>
          {/* Selection count badge */}
          {selectedNdcs.length > 0 && (
            <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-xs font-semibold text-primary-700 ring-1 ring-primary-200">
              {selectedNdcs.length} selected
            </span>
          )}

          {filteredPackagings.some(pkg => pkg.drug === null) && (
            <Button
              outline
              aria-label="Associate Selected Rows"
              disabled={selectedNdcs.length === 0}
              onClick={openAssociateNdcsDialog}
            >
              <HiOutlineLink/>
              Associate
            </Button>
          )}
          <Button
            outline
            aria-label="Delete Selected Rows"
            disabled={selectedNdcs.length === 0}
            onClick={toggleDeleteRowsDialog}
          >
            <HiOutlineTrash/>
            Delete
          </Button>
          <Button
            outline
            aria-label="Add Existing Drugs"
            onClick={toggleAddDrugPackagingsDialog}
          >
            <HiOutlinePlusCircle/>
            Add Drugs
          </Button>
          <Button
            outline
            aria-label="Recalculate"
            disabled={recalculating}
            onClick={onRecalculate}
          >
            {recalculating ? <Spinner/> : <HiOutlineCalculator/>}
            {recalculating ? 'Recalculating…' : 'Recalculate'}
          </Button>
          <Button
            outline
            aria-label="Import"
            onClick={() => openImportModal()}
          >
            <HiOutlineArrowUpTray/>
            Import
          </Button>
          <Button
            outline
            aria-label="Export to CSV"
            onClick={() => onExport()}
          >
            <HiOutlineArrowDownTray/>
            Export CSV
          </Button>
        </DrugMarketDetailsFilters>

        <RowPinSelector
          tip="Pin rows to the top of the results"
          selected={selectedRowPins}
          options={rowPinOptions}
          onPinSelectionChange={handleRowPinSelectionChange}
        />

        <div className="overflow-x-auto">
          {(loadingMarketBasket || filteredPackagings.length === 0) ? (
            <div className="relative min-h-32">
              {loadingMarketBasket && <TableLoadingOverlay/>}
              {!loadingMarketBasket && filteredPackagings.length === 0 && (
                <TableEmptyPlaceholder content="No Drug Packaging data is available."/>
              )}
            </div>
          ) : (
            <>
              <DataTable table={table}/>
              <DataPaginator table={table}/>
            </>
          )}
        </div>
      </div>

      {/* BEGIN DIALOGS */}
      {marketBasket && (
        <AddDrugPackagingsDialog
          open={showAddDrugPackagingsDialog}
          marketBasketId={marketBasket.id}
          onClose={toggleAddDrugPackagingsDialog}
          onSuccess={() => {
            toggleAddDrugPackagingsDialog();
            loadMarketBasket(marketBasket.id);
          }}
        />
      )}
      <AssociateNdcsDialog
        onSubmitComplete={handleAssociateUnknownPackagingsSuccess}
        onClose={toggleAssociateUnknownPackagingsDialog}
        open={showAssociateUnknownPackagingsDialog}
        selectedDrugPackagings={selectedDrugPackagings}
      />
      <InvalidNdcSelectionDialog
        open={showInvalidNdcSelectionDialog}
        onOk={toggleInvalidNdcSelectionDialog}
      />
      <DeleteNdcsDialog
        open={showDeleteRowsDialog}
        onCancel={toggleDeleteRowsDialog}
        onSubmit={requestDeleteRows}
        busy={deletingRows}
        selectedDrugPackagings={selectedDrugPackagings}
      />
      <CalculationLogDialog
        open={showCalculationLogDialog}
        onClose={toggleCalculationLogDialog}
        calculationDetails={calculationDetails}
      />
      <EditDrugPackagingDialog
        // @ts-expect-error TS(2322): Type 'MarketBasketDrugPackaging | undefined' is no... Remove this comment to see the full error message
        marketBasketDrugPackaging={drugPackagingToEdit}
        isOpen={showEditDrugPackagingDialog}
        onClose={cancelEditMarketBasketDrugPackaging}
        onSaved={handleEditMarketBasketDrugPackagingSuccess}
      />
      {/* END DIALOGS */}
    </div>
  );
};
